import type { operations } from '@luxuryescapes/contract-search';

import type { SearchReproductionRow } from './ReproduceSearchService';
import { json_headers, request } from './common';
import type { RequestOptions, SearchServiceEnvelope } from './common';

type RawAnalyticsSearchStatsResponse =
  operations['/api/search/admin/v1/searches/stats/get']['responses']['200']['content']['application/json']['result'];

type RawAnalyticsSearchCategoryResult = RawAnalyticsSearchStatsResponse['categories'][number];

type RawAnalyticsTopSearch = RawAnalyticsSearchCategoryResult['top_searches'][number];

export type AnalyticsSearchStatsParams = {
  startDate: string;
  endDate: string;
  limit: number;
};

export type AnalyticsTopSearch = Omit<SearchReproductionRow, 'naturalLanguageQuery'> & {
  rank: number;
  searchId: string;
  naturalLanguageQuery: string;
  queryCount: number;
};

export type AnalyticsSearchCategoryResult = {
  searchCategory: string;
  searchCount: number;
  topSearches: Array<AnalyticsTopSearch>;
};

export type AnalyticsDateRange = {
  startDate: string;
  endDate: string;
};

type AnalyticsSearchStatsResponse = {
  dateRange: AnalyticsDateRange;
  categories: Array<AnalyticsSearchCategoryResult>;
};

function searchAdminSearchesStatsPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/stats`;
}

function normaliseAnalyticsSearchCategoryResults(
  categories: Array<RawAnalyticsSearchCategoryResult>,
): Array<AnalyticsSearchCategoryResult> {
  return categories.map((result) => ({
    searchCategory: result.search_category,
    searchCount: result.search_count,
    topSearches: result.top_searches
      .filter(
        (search): search is RawAnalyticsTopSearch & { search_id: string; natural_language_query: string } =>
          search.search_id != null && search.natural_language_query != null,
      )
      .map((search) => ({
        brand: search.brand,
        region: search.region,
        platform: search.platform,
        language: null,
        searchType: null,
        destinations: search.destination_ids.map((id, index) => ({
          id,
          name: search.destination_names[index] ?? id,
          location: null,
        })),
        landmarks: search.landmark_ids.map((id, index) => ({
          id,
          name: search.landmark_names[index] ?? id,
          location: null,
        })),
        properties: search.property_ids.map((id, index) => ({
          id,
          name: search.property_names[index] ?? id,
          location: null,
        })),
        startDate: null,
        endDate: null,
        adults: null,
        children: null,
        sortValue: null,
        luxplusTier: null,
        loyaltyTier: null,
        utmSource: null,
        utmMedium: null,
        utmCampaign: null,
        filters: [],
        rank: search.rank,
        searchId: search.search_id,
        naturalLanguageQuery: search.natural_language_query,
        queryCount: search.query_count,
      })),
  }));
}

export function normaliseAnalyticsSearchStatsResponse(
  response: RawAnalyticsSearchStatsResponse,
): AnalyticsSearchStatsResponse {
  return {
    dateRange: {
      startDate: response.dateRange.startDate,
      endDate: response.dateRange.endDate,
    },
    categories: normaliseAnalyticsSearchCategoryResults(response.categories),
  };
}

export async function getAnalyticsSearchStats(
  params: AnalyticsSearchStatsParams,
  options: RequestOptions = {},
): Promise<AnalyticsSearchStatsResponse> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    limit: String(params.limit),
  }).toString();

  const response = (await request(`${searchAdminSearchesStatsPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<RawAnalyticsSearchStatsResponse>;

  return normaliseAnalyticsSearchStatsResponse(response.result);
}
